import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { AppButton, AppTextField } from "../form";
import { AppFormField, AppFormGrid, AppSlideOver } from "../layout";
import { GRID_FULL } from "../../lib/ui/formLayout";
import { useTranslation } from "react-i18next";
import ChartEditorPanel from "../ChartEditorPanel";
import WidgetOutputView from "../WidgetOutputView";
import { widgetService } from "../../services/widgetService";
import type { Widget, WidgetExecuteResponse } from "../../types/widget";
import {
  buildWidgetChartMapping,
  mappingToJson,
  parseWidgetChartMapping,
  type WidgetChartMapping,
} from "../../utils/chartMappingBuilder";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

type Props = {
  open: boolean;
  widgetId: string | null;
  titleOverride: string;
  dateRange: { from: string; to: string };
  onClose: () => void;
  onSaved: (widgetId: string, titleOverride?: string) => void | Promise<void>;
};

export default function DashboardWidgetEditDialog({
  open,
  widgetId,
  titleOverride,
  dateRange,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.dashboardWidgetEditDialog;
  const [widget, setWidget] = useState<Widget | null>(null);
  const [title, setTitle] = useState(titleOverride);
  const [chartMapping, setChartMapping] = useState<WidgetChartMapping>(buildWidgetChartMapping("bar"));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<WidgetExecuteResponse | null>(null);

  useEffect(() => {
    if (!open || !widgetId) return;
    setTitle(titleOverride);
    setPreviewResult(null);
    setError(null);
    setLoading(true);
    widgetService
      .get(widgetId)
      .then((w) => {
        setWidget(w);
        setChartMapping(parseWidgetChartMapping(w.output?.mapping ?? {}));
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [open, widgetId, titleOverride]);

  const isEcharts = (widget?.output?.format || "echarts") === "echarts";

  const buildWidgetPayload = useCallback((): Widget | null => {
    if (!widget) return null;
    const mapping = JSON.parse(mappingToJson(chartMapping)) as Record<string, unknown>;
    return {
      ...widget,
      output: {
        format: widget.output?.format || "echarts",
        mapping,
      },
    };
  }, [widget, chartMapping]);

  const onChartMappingChange = useCallback((next: WidgetChartMapping) => {
    setChartMapping(next);
    setPreviewResult(null);
  }, []);

  const onPreview = async () => {
    const payload = buildWidgetPayload();
    if (!payload) return;
    setPreviewing(true);
    setError(null);
    try {
      const res = await widgetService.preview(payload, dateRange);
      if (!res.valid && !res.sampleResult) {
        setError(res.errors?.join("; ") || res.error || t("widget.error.generic"));
        setPreviewResult(null);
        return;
      }
      if (!res.valid && res.errors?.length) {
        setError(res.errors.join("; "));
      }
      setPreviewResult({
        format: res.format || payload.output?.format || "echarts",
        payload: res.sampleResult,
        error: res.error,
        warnings: res.warnings,
      });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setPreviewResult(null);
    } finally {
      setPreviewing(false);
    }
  };

  const onSave = async () => {
    const payload = buildWidgetPayload();
    if (!payload?.id) return;
    setSaving(true);
    setError(null);
    try {
      await widgetService.update(payload.id, payload);
      await onSaved(payload.id, title.trim() || undefined);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppSlideOver
      open={open}
      onClose={onClose}
      size="xl"
      title={t("dashboard.widgetEditDialog")}
      actions={
        <>
          <AppButton variant="outlined" onClick={onClose} disabled={saving}>
            {t("common.cancel")}
          </AppButton>
          <AppButton onClick={() => void onSave()} disabled={loading || saving || !widget}>
            {saving ? t("common.saving") : t("common.save")}
          </AppButton>
        </>
      }
    >
        {loading && (
          <Stack alignItems="center" sx={styles.loadingStack}>
            <CircularProgress size={28} />
          </Stack>
        )}

        {!loading && widget && (
          <Stack spacing={2} sx={styles.formStack}>
            <Typography variant="body2" color="text.secondary">
              {widget.name}
            </Typography>

            <AppFormGrid>
              <AppFormField size={GRID_FULL}>
                <AppTextField
                  label={t("dashboard.titleOverrideLabel")}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  helperText={t("dashboard.titleOverrideHint")}
                />
              </AppFormField>
            </AppFormGrid>

            {isEcharts && (
              <ChartEditorPanel mapping={chartMapping} onChange={onChartMappingChange} disabled={saving} />
            )}

            {!isEcharts && (
              <Alert severity="info">{t("dashboard.chartEditNotAvailable")}</Alert>
            )}

            {isEcharts && (
              <Stack direction="row" spacing={1} alignItems="center">
                <AppButton size="small" variant="outlined" onClick={onPreview} disabled={previewing}>
                  {previewing ? t("widget.previewing") : t("widget.preview")}
                </AppButton>
                <Typography variant="caption" color="text.secondary">
                  {t("dashboard.widgetEditPreviewHint")}
                </Typography>
              </Stack>
            )}

            {previewResult && (
              <Box sx={styles.previewBox}>
                <WidgetOutputView result={previewResult} loading={previewing} />
              </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}

        {!loading && !widget && error && <Alert severity="error">{error}</Alert>}
    </AppSlideOver>
  );
}
